import { If } from '@paytrace/pt-skel/Component'
import { debounce } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ActionMenuItem } from '../../../../../Component/EnhancedData/ActionMenu'
import { ProgressBar } from '../../../../../Component/EnhancedData/ProgressBar'
import { SortableTable } from '../../../../../Component/EnhancedData/SortableTable'
import { tableColumnTypes } from '../../../../../Component/EnhancedData/SortableTable/TableBody'
import { DataWithKey, RowInputChangeHandler } from '../../../../../Component/EnhancedData/Types'
import { PencilIcon, TrashIcon } from '../../../../../Component/Icons'
import {
  color,
  CUSTOM_PRODUCT_NOT_FOUND_MSG,
  ED_FIELD_SPEC,
  initialProductItem,
  MAX_NO_OF_LINE_ITEMS,
  TABLE_ITEM_SCROLL_THRESHOLD,
  WAIT_TIME_FOR_CHANGE_DETECTION,
} from '../../../../../Helper/EnhancedData/Constants'
import {
  isEmptyLineItem,
  toConsumableNumber,
  toDataWithKey,
  updateLineItemRowErrorIndices,
} from '../../../../../Helper/EnhancedData/EnhancedDataUtils'
import { buildLineItemEmptyTableView } from '../../../../../Helper/EnhancedData/LineItemTableHelper'
import {
  toLineItemRows,
  toMappedActionMenu,
  toRowErrorElements,
  updateLineItemErrorMap,
} from '../../../../../Mapper/LineItemTableDataMapper'
import { toTableColumns } from '../../../../../Mapper/TableDataMapper'
import * as EDT from '../../../../../Type/EnhancedData'
import { LineItemRowErrorMap } from '../../../../../Type/EnhancedData'
import DiscountRow from './DiscountRow'
import { EnhancedDataTotalRow } from './EnhancedDataTotalRow'
import { EnhancedProductAction, EnhancedProductForm } from './EnhancedProductForm'
import FreightRow from './FreightRow'
import { MaxLineItemErrorRow } from './MaxLineItemErrorRow'
import ProductDropdown, { useProductForm } from './ProductDropdown'
import { SubtotalRow } from './SubtotalRow'
import SurchargeAmountRow from './SurchargeAmountRow'
import SurchargeTaxAmountRow from './SurchargeTaxAmountRow'
import TaxAmountRow from './TaxAmountRow'

const SHOW_MAX_LINE_ITEM_ERROR_FOR_SEC = 5

const _columnDefs: EDT.LineItemColumDefinition[] = [
  {
    id: 'rowNumber',
    displayName: '',
    dataType: tableColumnTypes.string,
  },
  {
    id: 'productName',
    displayName: ED_FIELD_SPEC.line_item.productName.displayName,
    dataType: tableColumnTypes.string,
    tooltip: ED_FIELD_SPEC.line_item.productName.tooltip,
    placeholder: 'Select or Create Product Item',
  },
  {
    id: 'quantity',
    displayName: 'Quantity',
    dataType: tableColumnTypes.numberTwoDecimals,
  },
  {
    id: 'unitOfMeasure',
    displayName: ED_FIELD_SPEC.line_item.unitOfMeasure.displayName,
    dataType: tableColumnTypes.string,
    tooltip: ED_FIELD_SPEC.line_item.unitOfMeasure.tooltip,
  },
  {
    id: 'costPerUnit',
    displayName: ED_FIELD_SPEC.line_item.costPerUnit.displayName,
    dataType: tableColumnTypes.money,
    tooltip: ED_FIELD_SPEC.line_item.costPerUnit.tooltip,
    dataPrefixIcon: '$',
  },
  {
    id: 'itemTax',
    displayName: ED_FIELD_SPEC.line_item.itemTax.displayName,
    dataType: tableColumnTypes.money,
    tooltip: ED_FIELD_SPEC.line_item.itemTax.tooltip,
    dataPrefixIcon: '$',
  },
  {
    id: 'itemDiscountAmount',
    displayName: ED_FIELD_SPEC.line_item.itemDiscountAmount.displayName,
    dataType: tableColumnTypes.money,
    tooltip: ED_FIELD_SPEC.line_item.itemDiscountAmount.tooltip,
    dataPrefixIcon: '$',
  },
  {
    id: 'amount',
    displayName: ED_FIELD_SPEC.line_item.amount.displayName,
    dataType: tableColumnTypes.string,
    tooltip: ED_FIELD_SPEC.line_item.amount.tooltip,
  },
]

// Shallow clone definitions to preserve column immutability across renders
const getColumnDefs = (selectedTaxOption: EDT.TaxOptionType): EDT.LineItemColumDefinition[] => {
  const defs = (selectedTaxOption === 'transaction' || selectedTaxOption === 'exempt')
    ? _columnDefs.filter(col => col.id !== 'itemTax')
    : _columnDefs

  return defs.map(col => ({ ...col }))
}

export const addSearchIconToProductName = (
  columnDefs: EDT.LineItemColumDefinition[],
  searchResultsMap: Record<string | number, EDT.LineItemStateData[]>,
  hasMoreProductsMap: Record<string | number, boolean>,
  onProductSelect: (key: string | number, product: EDT.LineItemStateData) => void,
  onTriggerInlineCreate: (key: string | number) => void,
  isRowPopulated: (key: string | number) => boolean,
  onLoadMore: (rowKey: string | number, nextOffset: number) => void,
  activeDropdownRowKey?: string | number | null,
  setActiveDropdownRowKey?: (key: string | number | null) => void
): void => {
  const def = columnDefs.find(col => col.id === 'productName')
  if (def) {
    def.dataSuffixIcon = (
      <ProductDropdown
        searchResultsMap={searchResultsMap}
        hasMoreProductsMap={hasMoreProductsMap}
        onProductSelect={onProductSelect}
        onTriggerInlineCreate={onTriggerInlineCreate}
        isRowPopulated={isRowPopulated}
        onLoadMore={onLoadMore}
        activeDropdownRowKey={activeDropdownRowKey}
        setActiveDropdownRowKey={setActiveDropdownRowKey}
      />
    )
  }
}

const buildActionMenuItem = (
  lineItemMap: Record<string, DataWithKey<EDT.LineItemStateData>>,
  handleDelete: (row: DataWithKey<EDT.LineItemStateData>) => void,
  handleEdit: (key: string | number) => void,
  isRowPopulated: (key: string | number) => boolean
): Record<string, React.JSX.Element> => {
  const mappedMenu: Record<string, React.JSX.Element> = {}

  Object.keys(lineItemMap).forEach((key) => {
    const row: DataWithKey<EDT.LineItemStateData> = lineItemMap[key]
    const rowActions: ActionMenuItem[] = [
      { label: 'Delete', icon: <TrashIcon/>, onClick: () => handleDelete(row), color: color.RED },
    ]
    if (isRowPopulated(key)) {
      rowActions.push({
        label: 'Edit product',
        icon: <PencilIcon/>,
        onClick: () => handleEdit(key),
      })
    }
    const singleRowMap = toMappedActionMenu({ [key]: row }, rowActions)
    Object.assign(mappedMenu, singleRowMap)
  })

  return mappedMenu
}

export function LineItemsTable (props: EDT.L3SectionProps): React.JSX.Element {
  const {
    formData,
    selectedTaxOption,
    searchResultsMap,
    hasMoreProductsMap,
    onLineItemAdd,
    onLineItemDelete,
    onLineItemUpdate,
    onSearchKeywordChange,
    onInputChange,
    onLineItemRowError,
    onEnhancedDataTotalChange,
    onLoadMore,
  } = props

  const { lineItemMap = {} } = formData || {}
  const currentRowCount = Object.keys(lineItemMap).length || 0
  const hasRowData: boolean = currentRowCount > 0
  const isTaxPerLineItemSelected = props.selectedTaxOption === 'line-item'

  const [activeDropdownRowKey, setActiveDropdownRowKey] = useState<string | number | null>(null)
  const [editingRowKey, setEditingRowKey] = useState<string | number | null>(null)
  const [rowErrorMap, setRowErrorMap] = useState<LineItemRowErrorMap>({})
  const [showMaxLineItemError, setShowMaxLineItemError] = useState<boolean>(false)

  const { formData: inlineFormData, handleInputChange, setFormData } = useProductForm(initialProductItem)

  // 1. Core Handlers
  const isRowPopulated = useCallback((key: string | number): boolean => {
    const row = lineItemMap[key]
    return !!(row && row.productName && row.productName.trim() !== '')
  }, [lineItemMap])

  const getProductAction = (key: string | number): EnhancedProductAction => {
    const isRowEmpty: boolean = isEmptyLineItem(lineItemMap[key])
    return isRowEmpty ? EnhancedProductAction.CREATE_NEW : EnhancedProductAction.EDIT_EXISTING
  }

  // *** Calculation units ***
  // Calculate current subtotal
  const currentSubtotal: number = useMemo(() => {
    return Object.values(lineItemMap).reduce((sum, row) => {
      return sum + (Number(row.amount) || 0)
    }, 0)
  }, [lineItemMap])

  // Calculate Tax amount per line-items
  const currentTaxAmountTotal: number = useMemo(() => {
    return Object.values(lineItemMap).reduce((sum, row) => {
      return sum + (Number(row.itemTax) || 0)
    }, 0)
  }, [lineItemMap])

  // Calculate enhanced data total
  const currentEnhancedDataTotal: number = useMemo(() => {
    const { taxAmount, discountAmount, freightAmount, surchargeAmount, surchargeTaxAmount } = props.formData

    const tax = toConsumableNumber(taxAmount)
    const discount = toConsumableNumber(discountAmount)
    const freight = toConsumableNumber(freightAmount)
    const surcharge = toConsumableNumber(surchargeAmount)
    const surchargeTax = isTaxPerLineItemSelected ? toConsumableNumber(surchargeTaxAmount) : 0

    return currentSubtotal + tax + freight + surcharge + surchargeTax - discount
  }, [currentSubtotal, isTaxPerLineItemSelected, props.formData])

  const handleLineItemAdd = useCallback(() => {
    if (currentRowCount < MAX_NO_OF_LINE_ITEMS) {
      onLineItemAdd(toDataWithKey(initialProductItem))
    } else {
      setShowMaxLineItemError(true)
    }
  }, [currentRowCount, onLineItemAdd])

  const handleLineItemDelete = useCallback((row: DataWithKey<EDT.LineItemStateData>) => {
    const updatedLineItemMap = onLineItemDelete(row.key)
    setRowErrorMap(prev => {
      const { [String(row.key)]: removedError, ...restRowErrorMap } = prev
      return updateLineItemRowErrorIndices(updatedLineItemMap, restRowErrorMap)
    })
  }, [onLineItemDelete])

  const handleTriggerInlineCreate = useCallback((key: string | number) => {
    setEditingRowKey(key)
    const existingRow = lineItemMap[key]
    setFormData({
      productLabel: existingRow?.productName || '',
      productName: existingRow?.productName || '',
      quantity: existingRow?.quantity || 0,
      amount: existingRow?.amount || 0,
      productCode: existingRow?.productCode || '',
      commodityCode: existingRow?.commodityCode || '',
      unitOfMeasure: existingRow?.unitOfMeasure || '',
      costPerUnit: existingRow?.costPerUnit || 0,
      itemTax: existingRow?.itemTax || 0,
      itemDiscountAmount: existingRow?.itemDiscountAmount || 0,
      index: existingRow?.index || -1,
      rowNumber: existingRow?.rowNumber || 0,
    })
  }, [lineItemMap, setFormData])

  const handleProductLoadOnScroll = useCallback((rowKey: string | number, nextOffset: number) => {
    const currentKeyword = lineItemMap[rowKey]?.productName || ''
    if (currentKeyword) {
      onSearchKeywordChange?.(currentKeyword, rowKey, nextOffset)
    } else if (onLoadMore) {
      onLoadMore(rowKey, nextOffset)
    }
  }, [lineItemMap, onSearchKeywordChange, onLoadMore])

  // Debounce search keyword changes using SEARCH_PRODUCT_DEBOUNCE_MS
  const debouncedSearchKeywordChange = useMemo(() => {
    return debounce((value: string, key: string | number) => {
      if (onSearchKeywordChange) {
        onSearchKeywordChange(value, key, 0)
      }
    }, WAIT_TIME_FOR_CHANGE_DETECTION)
  }, [onSearchKeywordChange])

  useEffect(() => {
    return () => {
      debouncedSearchKeywordChange.cancel()
    }
  }, [debouncedSearchKeywordChange])

  const handleLineItemUpdate: RowInputChangeHandler<EDT.LineItemStateData> = useCallback(
    (rowKey: string | number, fieldName: string, value: unknown) => {
      const stringRowKey = String(rowKey)

      // 1. Debounce product search when typing in the productName input
      if (fieldName === 'productName' && typeof value === 'string') {
        setActiveDropdownRowKey(stringRowKey)
        debouncedSearchKeywordChange(value, stringRowKey)
      }

      // 2. Dispatch the standard single field update
      onLineItemUpdate?.(stringRowKey, fieldName, value)

      // 3. Auto-calculate amount when quantity or costPerUnit changes
      if (['quantity', 'costPerUnit', 'itemDiscountAmount'].includes(fieldName)) {
        const currentRow = lineItemMap[stringRowKey]
        if (!currentRow) return

        const tempRow = { ...currentRow, [fieldName]: value }
        const qty = tempRow.quantity ?? 0
        const cost = tempRow.costPerUnit ?? 0
        const discount = tempRow.itemDiscountAmount ?? 0
        const computedAmount = toConsumableNumber(qty * cost - discount)
        if (tempRow.amount !== computedAmount) {
          onLineItemUpdate?.(stringRowKey, 'amount', computedAmount)
        }
      }
    },
    [onLineItemUpdate, debouncedSearchKeywordChange, lineItemMap]
  )

  // Inside LineItemsTable or column definition wrapper
  const handleProductSelect = useCallback(
    (rowKey: string | number, selectedProduct: EDT.LineItemStateData) => {
      const stringRowKey = String(rowKey)
      const currentRow = lineItemMap[stringRowKey]
      if (!currentRow) return

      const qty = toConsumableNumber(currentRow.quantity ?? 0)
      const cost = toConsumableNumber(selectedProduct.costPerUnit ?? 0)
      const computedAmount = toConsumableNumber(qty * cost)

      const productToCreate: DataWithKey<EDT.LineItemStateData> = toDataWithKey(
        {
          ...selectedProduct,
          index: currentRow.index,
          rowNumber: currentRow.rowNumber,
          amount: computedAmount,
        },
        rowKey
      )
      setActiveDropdownRowKey(null)
      onLineItemAdd(productToCreate)
    },
    [lineItemMap, onLineItemAdd]
  )

  // 2. Column Definitions with lazy loading and hasMore forwarding
  const columnDefs = useMemo(() => {
    const defs = getColumnDefs(selectedTaxOption)

    addSearchIconToProductName(
      defs,
      searchResultsMap || {},
      hasMoreProductsMap || {},
      handleProductSelect,
      handleTriggerInlineCreate,
      isRowPopulated,
      handleProductLoadOnScroll,
      activeDropdownRowKey,
      setActiveDropdownRowKey
    )

    return defs
  }, [
    selectedTaxOption,
    searchResultsMap,
    hasMoreProductsMap,
    handleProductSelect,
    handleTriggerInlineCreate,
    isRowPopulated,
    handleProductLoadOnScroll,
    activeDropdownRowKey,
  ])

  const handleInlineSave = (rowKey: string | number) => {
    const itemTaxAmount = toConsumableNumber(inlineFormData?.itemTax)
    const computedAmount = toConsumableNumber(inlineFormData.quantity * inlineFormData.costPerUnit)
    const existingLineItem = lineItemMap[rowKey] || {}

    // clearing search result cache
    delete searchResultsMap?.[rowKey]

    const updatedProductLineItem: EDT.LineItemStateData = {
      productName: inlineFormData.productLabel || '',
      quantity: inlineFormData.quantity,
      unitOfMeasure: inlineFormData.unitOfMeasure || '',
      costPerUnit: inlineFormData.costPerUnit,
      commodityCode: inlineFormData.commodityCode || '',
      productCode: inlineFormData.productCode || '',
      itemTax: itemTaxAmount,
      amount: computedAmount,
      itemDiscountAmount: inlineFormData.itemDiscountAmount,
      index: existingLineItem ? existingLineItem.index : inlineFormData.index,
      rowNumber: existingLineItem ? existingLineItem.rowNumber : inlineFormData.rowNumber,
    }

    onLineItemAdd(toDataWithKey(updatedProductLineItem, String(rowKey)))
    setEditingRowKey(null)
  }

  const handleRowError = useCallback(
    (rowKey: string | number, fieldName: string, errorMessage: string | null, isVisible: boolean) => {
      const currentLineItem: EDT.LineItemStateData = lineItemMap[rowKey]
      if (!currentLineItem) return
      setRowErrorMap(prevRowErrorMaps => {
        return updateLineItemErrorMap(
          prevRowErrorMaps,
          rowKey,
          fieldName,
          errorMessage,
          currentLineItem.index,
          currentLineItem.rowNumber,
          isVisible,
        )
      })
    }, [lineItemMap])

  // Detect completed searches with 0 results and set 'No Product Found'
  const evaluateSearchResult = useCallback(() => {
    if (!searchResultsMap) return
    Object.entries(lineItemMap).forEach(([rowKey, lineItem]) => {
      const rawSearchQuery = lineItem?.productName?.trim() || ''
      const rowSearchResults = searchResultsMap[rowKey]
      const currentErrorMsg = rowErrorMap[rowKey]?.fieldErrorMap?.productName?.message
      const errorAlreadySet = currentErrorMsg === CUSTOM_PRODUCT_NOT_FOUND_MSG

      const hasSearchInput = rawSearchQuery.length > 0
      const hasEmptyResults = Array.isArray(rowSearchResults) && rowSearchResults.length === 0
      const hasValidResults = Array.isArray(rowSearchResults) && rowSearchResults.length > 0
      const isSearchPendingOrIdle = rowSearchResults === undefined

      // Case A: Query typed AND search results array is empty [] (no matches found)
      if (!errorAlreadySet && hasSearchInput && hasEmptyResults) {
        handleRowError(rowKey, 'productName', CUSTOM_PRODUCT_NOT_FOUND_MSG, true)
      } else if (errorAlreadySet && (rawSearchQuery.length === 0 || hasValidResults || isSearchPendingOrIdle)) {
        handleRowError(rowKey, 'productName', null, false)
      }
    })
  }, [handleRowError, lineItemMap, rowErrorMap, searchResultsMap])

  useEffect(() => { evaluateSearchResult() }, [evaluateSearchResult])

  const rowErrorContentMap = useMemo(() => toRowErrorElements(rowErrorMap), [rowErrorMap])

  const hideMaxLineItemErrorAfterInterval = useCallback(() => {
    if (!showMaxLineItemError) return
    const timer = setTimeout(
      () => setShowMaxLineItemError(false), SHOW_MAX_LINE_ITEM_ERROR_FOR_SEC * 1000
    )
    return () => clearTimeout(timer)
  }, [showMaxLineItemError])

  // hide max line item validation error after a certain duration
  useEffect(() => {
    return hideMaxLineItemErrorAfterInterval()
  }, [hideMaxLineItemErrorAfterInterval])

  // Direct trigger on focus: immediately opens this row's dropdown and triggers empty-keyword search
  const handleRowFocus = useCallback((rowKey: string | number, fieldName: string) => {
    if (fieldName === 'productName') {
      setActiveDropdownRowKey(rowKey)
      onSearchKeywordChange?.('', rowKey, 0)
    }
  }, [onSearchKeywordChange])

  const tableRows = useMemo(() => {
    return toLineItemRows(lineItemMap, columnDefs, handleLineItemUpdate, handleRowError, handleRowFocus)
  }, [lineItemMap, columnDefs, handleLineItemUpdate, handleRowError, handleRowFocus])

  // callback the parent with line item row error
  useEffect(() => {
    if (onLineItemRowError) onLineItemRowError(rowErrorMap)
  }, [onLineItemRowError, rowErrorMap])

  // Call back when Enhanced Data Total changes to set total-check message
  useEffect(() => {
    if (onEnhancedDataTotalChange) {
      onEnhancedDataTotalChange(currentEnhancedDataTotal)
    }
  }, [onEnhancedDataTotalChange, currentEnhancedDataTotal])

  return (
    <div className="line-item-table-container">
      <ProgressBar
        enhancedDataTotal={currentEnhancedDataTotal}
        originalTotal={props.transactionAmount || 0}
      />

      <SortableTable
        columns={toTableColumns(columnDefs)}
        rows={tableRows}
        onSortClick={() => {}}
        className={'line-item-table'}
        displayEmptyTableView={!hasRowData}
        emptyTableView={buildLineItemEmptyTableView(handleLineItemAdd)}
        actionComponentMap={buildActionMenuItem(lineItemMap, handleLineItemDelete, handleTriggerInlineCreate, isRowPopulated)}
        editingRowKey={editingRowKey}
        onCancelInlineForm={() => setEditingRowKey(null)}
        renderInlineForm={(rowKey, onCancel) => {
          return (
            <EnhancedProductForm
              productAction={getProductAction(rowKey)}
              initialValues={inlineFormData}
              changeHandler={handleInputChange}
              onSave={() => handleInlineSave(rowKey)}
              onCancel={onCancel}
            />
          )
        }}
        rowErrorContentMap={rowErrorContentMap}
        highlightRowsOnError={props.highlightRowsOnError || false}
        scrollThreshold={TABLE_ITEM_SCROLL_THRESHOLD}
      >
        <tfoot>
        <If is={showMaxLineItemError}>
          <MaxLineItemErrorRow
            message={`You cannot add more than ${MAX_NO_OF_LINE_ITEMS} product line items to a single transaction.`}
          />
        </If>
        <SubtotalRow itemCount={ currentRowCount } subtotal={currentSubtotal} onAddClick={handleLineItemAdd}/>
        <TaxAmountRow
          value={formData?.taxAmount || 0}
          onInputChange={onInputChange}
          onInputError={props.onInputError}
          selectedTaxOption={selectedTaxOption}
          totalTaxAmount={currentTaxAmountTotal}
          fieldDefinition={{
            key: 'taxAmount',
            name: 'taxAmount',
            label: ED_FIELD_SPEC.level3.taxAmount.displayName,
            tooltip: ED_FIELD_SPEC.level3.taxAmount.tooltip,
            showRequired: false,
            dataType: tableColumnTypes.money,
            pattern: ED_FIELD_SPEC.level3.taxAmount.pattern,
            errorMessage: ED_FIELD_SPEC.level3.taxAmount.errorMessage,
          }} />
        <SurchargeAmountRow
          value={formData?.surchargeAmount ?? 0}
          onInputChange={onInputChange}
          onInputError={props.onInputError}
          fieldDefinition={{
            key: 'surchargeAmount',
            name: 'surchargeAmount',
            label: ED_FIELD_SPEC.level3.surchargeAmount.displayName,
            tooltip: ED_FIELD_SPEC.level3.surchargeAmount.tooltip,
            showRequired: false,
            dataType: tableColumnTypes.money,
            pattern: ED_FIELD_SPEC.level3.surchargeAmount.pattern,
            errorMessage: ED_FIELD_SPEC.level3.surchargeAmount.errorMessage,
          }} />
        <If is={isTaxPerLineItemSelected}>
          <SurchargeTaxAmountRow
          value={formData?.surchargeTaxAmount ?? 0}
          surchargeAmount={formData?.surchargeAmount ?? 0}
          onInputChange={onInputChange}
          onInputError={props.onInputError}
          fieldDefinition={{
            key: 'surchargeTaxAmount',
            name: 'surchargeTaxAmount',
            label: ED_FIELD_SPEC.level3.surchargeTaxAmount.displayName,
            tooltip: ED_FIELD_SPEC.level3.surchargeTaxAmount.tooltip,
            showRequired: false,
            dataType: tableColumnTypes.money,
            pattern: ED_FIELD_SPEC.level3.surchargeTaxAmount.pattern,
            errorMessage: ED_FIELD_SPEC.level3.surchargeTaxAmount.errorMessage,
          }} />
        </If>
        <DiscountRow
          value={formData?.discountAmount || 0}
          onInputChange={onInputChange}
          onInputError={props.onInputError}
          fieldDefinition={{
            key: 'discountAmount',
            name: 'discountAmount',
            label: ED_FIELD_SPEC.level3.discountAmount.displayName,
            tooltip: ED_FIELD_SPEC.level3.discountAmount.tooltip,
            showRequired: false,
            dataType: tableColumnTypes.money,
            pattern: ED_FIELD_SPEC.level3.discountAmount.pattern,
            errorMessage: ED_FIELD_SPEC.level3.discountAmount.errorMessage,
          }} />
        <FreightRow
          value={formData?.freightAmount || 0}
          onInputChange={onInputChange}
          onInputError={props.onInputError}
          fieldDefinition={{
            key: 'freightAmount',
            name: 'freightAmount',
            label: ED_FIELD_SPEC.level3.freightAmount.displayName,
            tooltip: ED_FIELD_SPEC.level3.freightAmount.tooltip,
            showRequired: false,
            dataType: tableColumnTypes.money,
            pattern: ED_FIELD_SPEC.level3.freightAmount.pattern,
            errorMessage: ED_FIELD_SPEC.level3.freightAmount.errorMessage,
          }} />
        <EnhancedDataTotalRow total={currentEnhancedDataTotal} hasMatchingTotals={props.hasMatchingTotals}/>
        </tfoot>
      </SortableTable>
    </div>
  )
}
